import { CaseStudyConfig } from '../screens/CaseStudy/CaseStudy';

export interface ImageInfo {
  src: string;
  alt: string;
  width?: number;
  height?: number;
  aspectRatio?: number;
}

export interface VideoInfo {
  src: string;
  alt: string;
  type: 'mp4' | 'youtube';
  videoId?: string; // For YouTube videos
}

export interface MediaModule {
  src: string;
  alt: string;
  type: 'single' | 'twoColumn' | 'video';
  pairIndex?: number;
  videoId?: string;
  videoType?: 'mp4' | 'youtube';
}

/**
 * Detects aspect ratio of an image by loading it
 */
export const detectImageAspectRatio = (src: string): Promise<number> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const aspectRatio = img.width / img.height;
      resolve(aspectRatio);
    };
    img.onerror = () => reject(new Error(`Failed to load image: ${src}`));
    img.src = src;
  });
};

/**
 * Determines if an image is portrait (aspect ratio < 1)
 */
export const isPortrait = (aspectRatio: number): boolean => {
  return aspectRatio < 1;
};

/**
 * Determines if an image is landscape (aspect ratio > 1)
 */
export const isLandscape = (aspectRatio: number): boolean => {
  return aspectRatio > 1;
};

/**
 * Determines if an image is square (aspect ratio ≈ 1)
 */
export const isSquare = (aspectRatio: number, tolerance: number = 0.1): boolean => {
  return Math.abs(aspectRatio - 1) < tolerance;
};

/**
 * Automatically generates case study modules based on image aspect ratios
 */
export const generateCaseStudyModules = async (
  images: ImageInfo[],
  videos: VideoInfo[] = [],
  videoPosition: number = 1 // Position to insert videos (0-based, after hero image)
): Promise<MediaModule[]> => {
  const modules: MediaModule[] = [];
  
  // Detect aspect ratios for all images
  const imageData = await Promise.all(
    images.map(async (img) => ({
      ...img,
      aspectRatio: await detectImageAspectRatio(img.src)
    }))
  );

  // Add hero image (first image)
  if (imageData.length > 0) {
    modules.push({
      src: imageData[0].src,
      alt: imageData[0].alt,
      type: 'single'
    });
  }

  // Insert videos at specified position
  if (videos.length > 0 && videoPosition > 0) {
    videos.forEach((video) => {
      modules.push({
        src: video.src,
        alt: video.alt,
        type: 'video',
        videoId: video.videoId,
        videoType: video.type
      });
    });
  }

  // Process remaining images (skip first as it's the hero)
  const remainingImages = imageData.slice(1);
  let pairIndex = 0;

  for (let i = 0; i < remainingImages.length; i++) {
    const currentImage = remainingImages[i];
    
    // Check if current image is portrait and if there's a next image that's also portrait
    if (isPortrait(currentImage.aspectRatio) && 
        i + 1 < remainingImages.length && 
        isPortrait(remainingImages[i + 1].aspectRatio)) {
      
      // Create two-column layout for portrait pairs
      modules.push({
        src: currentImage.src,
        alt: currentImage.alt,
        type: 'twoColumn',
        pairIndex: pairIndex
      });
      
      modules.push({
        src: remainingImages[i + 1].src,
        alt: remainingImages[i + 1].alt,
        type: 'twoColumn',
        pairIndex: pairIndex
      });
      
      pairIndex++;
      i++; // Skip next image as it's already processed
    } else {
      // Single image (landscape, square, or single portrait)
      modules.push({
        src: currentImage.src,
        alt: currentImage.alt,
        type: 'single'
      });
    }
  }

  return modules;
};

/**
 * Creates a case study configuration with automatic layout detection
 */
export const createFlexibleCaseStudyConfig = async (
  storageKey: string,
  title: string,
  description: string,
  images: ImageInfo[],
  videos: VideoInfo[] = [],
  videoPosition: number = 1,
  creditsTitle: string = 'Credits',
  creditsCopy: string = ''
): Promise<CaseStudyConfig> => {
  const modules = await generateCaseStudyModules(images, videos, videoPosition);
  
  return {
    storageKey,
    title,
    description,
    modules,
    creditsTitle,
    creditsCopy
  };
};
